import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getSearchTermNegativeApply,
  listSearchTermNegativeApplies,
  type NegativeApplyRecord,
} from '@/services/googleAdsWeeklyService';

/** Poll the apply every 5 s while its outcome is unknown. */
export const APPLY_POLL_INTERVAL_MS = 5_000;
/** A key the server still has not heard of after this long never arrived. */
export const APPLY_NOT_FOUND_AFTER_MS = 30_000;

/** One apply decision = one key (a confirm dialog, or one Yes click). */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r % 4) + 8).toString(16);
  });
}

export type ApplyTracking =
  | { kind: 'idle' }
  | { kind: 'checking'; key: string; since: number; startedAt?: string; stage?: string }
  | { kind: 'never_reached'; key: string };

function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } } | null)?.response?.status;
}

/**
 * Follows an apply whose outcome the browser does not know (timed out,
 * dropped, edge 5xx, or someone else's apply is running) by polling the
 * ledger by key until it finishes, then calls ``onTerminal`` exactly once
 * per key. Also reports an apply already running when the tab mounts.
 */
export function useNegativeApplyTracker(
  tenantId: string,
  onTerminal: (record: NegativeApplyRecord) => void,
) {
  const queryClient = useQueryClient();
  const [tracking, setTracking] = useState<ApplyTracking>({ kind: 'idle' });
  const handled = useRef<Set<string>>(new Set());
  const terminal = useRef(onTerminal);
  terminal.current = onTerminal;

  const checkingKey = tracking.kind === 'checking' ? tracking.key : null;
  const poll = useQuery({
    queryKey: ['google-ads-negative-apply', tenantId, checkingKey],
    queryFn: () => getSearchTermNegativeApply(tenantId, checkingKey as string),
    enabled: Boolean(tenantId && checkingKey),
    refetchInterval: APPLY_POLL_INTERVAL_MS,
    refetchOnWindowFocus: false,
    retry: false,
    gcTime: 0,
  });

  useEffect(() => {
    if (tracking.kind !== 'checking') return;
    const record = poll.data;
    if (!record || record.idempotency_key !== tracking.key) return;
    if (record.status === 'running') return;
    if (handled.current.has(tracking.key)) return;
    handled.current.add(tracking.key);
    setTracking({ kind: 'idle' });
    terminal.current(record);
  }, [poll.data, tracking]);

  useEffect(() => {
    if (tracking.kind !== 'checking' || !poll.isError) return;
    if (httpStatus(poll.error) !== 404) return;
    if (Date.now() - tracking.since > APPLY_NOT_FOUND_AFTER_MS) {
      setTracking({ kind: 'never_reached', key: tracking.key });
    }
  }, [poll.error, poll.errorUpdatedAt, poll.isError, tracking]);

  // An apply already running (another tab, another operator, a reload).
  const active = useQuery({
    queryKey: ['google-ads-negative-applies-active', tenantId],
    queryFn: () => listSearchTermNegativeApplies(tenantId, { active: true }),
    enabled: Boolean(tenantId) && tracking.kind === 'idle',
    refetchInterval: (query) =>
      (query.state.data?.length ?? 0) > 0 ? APPLY_POLL_INTERVAL_MS : false,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const activeApply = active.data?.[0] ?? null;
  const hadActive = useRef(false);
  useEffect(() => {
    if (activeApply) {
      hadActive.current = true;
    } else if (hadActive.current) {
      hadActive.current = false;
      // It finished: show what it changed.
      queryClient.invalidateQueries({
        queryKey: ['google-ads-search-terms-negatives', tenantId],
      });
    }
  }, [activeApply, queryClient, tenantId]);

  const record = tracking.kind === 'checking' && poll.data?.idempotency_key === tracking.key
    ? poll.data
    : undefined;
  return {
    tracking,
    /** Follow this key until its apply finishes. */
    check: (key: string, info: { startedAt?: string; stage?: string } = {}) =>
      setTracking({ kind: 'checking', key, since: Date.now(), ...info }),
    reset: () => setTracking({ kind: 'idle' }),
    /** Started-at and last finished stage, freshest source first. */
    startedAt: record?.started_at
      ?? (tracking.kind === 'checking' ? tracking.startedAt : undefined),
    stage: record?.progress?.stage
      ?? (tracking.kind === 'checking' ? tracking.stage : undefined),
    since: tracking.kind === 'checking' ? tracking.since : undefined,
    /** Polling failed for a reason other than "not there yet". */
    lostContact: tracking.kind === 'checking' && poll.isError && httpStatus(poll.error) !== 404,
    activeApply,
    busy: tracking.kind !== 'idle' || activeApply !== null,
  };
}
